
/**
 * Location saves API — mirrors the post-saves pattern but operates on canonical
 * `locations._id` values instead of post ids.
 *
 * Backend contract: `/api/v2/location-save`, `/api/v2/location-unsave`,
 * `/api/v2/saves/locations` and related count/savers endpoints.
 *
 * Reference: globe-thrivers-app-backend/docs/frontend/LOCATION_SAVES_GUIDE.md
 */

import axios from 'axios';
import Location from '@/models/location';
import { debug, error } from '@/utils/logger';
import { getReferralCreatorIdForApi } from '@/lib/analyticsAttribution';
import {
  shouldSkipGuestAuthenticatedApiCalls,
  shouldUseOpenBrowseGuestEndpoints,
  type PlatformBrowseMode,
} from '@/constants/platformBrowseMode';
import { mongoObjectIdStringOrNull } from '@/utils/idGeneration';

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;

const LOCATION_SAVE = `${API_URL}/api/v2/location-save`;
const LOCATION_UNSAVE = `${API_URL}/api/v2/location-unsave`;
const SAVED_LOCATIONS_FEED = `${API_URL}/api/v2/saves/locations`;

// ─── Types ────────────────────────────────────────────────────────────────────

/** One item returned by GET /api/v2/saves/locations */
export interface SavedLocationFeedItem {
  location_id: string;
  created_at: string;
  source_post_id: string | null;
  source_post_location_id: string | null;
  /** Hydrated location document (subset of `locations` collection) */
  location: {
    _id: string;
    name?: string;
    google_place_id?: string;
    a3_intl_address?: string;
    google_places_data?: Record<string, unknown>;
    photos?: Array<{ name?: string; photo_reference?: string; url?: string }>;
    image_urls?: string[];
    [key: string]: unknown;
  };
}

export interface SaveLocationParams {
  locationId: string;
  sourcePostId?: string;
  sourcePostLocationId?: string;
}

export interface SaveLocationResponse {
  acknowledged: boolean;
  insertedId: string;
}

export interface UnsaveLocationResponse {
  acknowledged: boolean;
  deletedCount: number;
}

export interface FetchSavedLocationsOptions {
  userId?: string;
  limit?: number;
  skip?: number;
}

export interface LocationSaveCountResponse {
  count: number;
}

export interface LocationSaversItem {
  user_id: string;
  created_at: string;
}

export interface LocationSaversResponse {
  count: number;
  saves: LocationSaversItem[];
}

export interface UserLocationSavesOutboundCountResponse {
  total_location_saves: number;
}

/** Response from GET /api/v2/user/:userId/location-saves/received/count */
export interface UserLocationSavesReceivedCountResponse {
  total_location_saves_received: number;
}

/** One row from GET /api/v2/user/:userId/location-saves/received */
export interface LocationSaveReceivedItem {
  location_id: string;
  source_post_id: string | null;
  source_post_location_id: string | null;
  /** The user who saved this location. */
  saver_user_id: string;
  created_at: string;
}

/** Response from GET /api/v2/user/:userId/location-saves/received */
export interface UserLocationSavesReceivedResponse {
  total: number;
  saves: LocationSaveReceivedItem[];
}

export interface FetchUserLocationSavesReceivedOptions {
  /** When false, includes the creator's own saves. Default is true (backend default). */
  excludeOwn?: boolean;
  limit?: number;
  skip?: number;
}

/** V2 auth headers required for inbound "received" routes. */
function getV2AuthHeaders(): Record<string, string> {
  const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  if (!rawAccessToken) {
    error('[locationSaves:getV2AuthHeaders] No access token found');
    throw new Error('Authentication token not found. User must be logged in.');
  }
  const accessToken = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;
  const headers: Record<string, string> = { Authorization: accessToken };
  const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  if (rawIdToken) {
    const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
    headers['x-id-token'] = idToken;
  }
  return headers;
}

// ─── Error helpers ────────────────────────────────────────────────────────────

interface LocationSaveApiError extends Error {
  status?: number;
  code?: string;
}

function buildApiError(
  err: unknown,
  fallbackMessage: string,
): LocationSaveApiError {
  const axiosErr = axios.isAxiosError(err) ? err : null;
  const status = axiosErr?.response?.status;
  const code = (axiosErr?.response?.data as Record<string, unknown>)?.code as string | undefined;
  const message =
    (axiosErr?.response?.data as Record<string, unknown>)?.message as string ||
    axiosErr?.message ||
    fallbackMessage;

  const apiError: LocationSaveApiError = new Error(message);
  apiError.status = status;
  apiError.code = code;
  return apiError;
}

// ─── API functions ────────────────────────────────────────────────────────────

/**
 * Bookmark a canonical location.
 *
 * Idempotent: re-saving refreshes `created_at` (server deletes old row first).
 * Pass `sourcePostId` / `sourcePostLocationId` when available for provenance tracking.
 */
export async function saveLocation(
  params: SaveLocationParams,
): Promise<SaveLocationResponse> {
  const { locationId, sourcePostId, sourcePostLocationId } = params;
  try {
    debug('[saveLocation] Bookmarking location', {
      locationId,
      sourcePostId: sourcePostId ?? null,
      sourcePostLocationId: sourcePostLocationId ?? null,
    });

    const body: Record<string, string> = { locationId };
    const validatedSourcePostId = mongoObjectIdStringOrNull(sourcePostId);
    const validatedSourcePostLocationId = mongoObjectIdStringOrNull(sourcePostLocationId);
    if (validatedSourcePostId) body.source_post_id = validatedSourcePostId;
    if (validatedSourcePostLocationId) {
      body.source_post_location_id = validatedSourcePostLocationId;
    }
    const referralCreatorId = getReferralCreatorIdForApi();
    if (referralCreatorId) body.referral_creator_id = referralCreatorId;

    const response = await axios.post<SaveLocationResponse>(LOCATION_SAVE, body);

    debug('[saveLocation] Location bookmarked', {
      locationId,
      insertedId: response.data.insertedId,
    });

    return response.data;
  } catch (err) {
    const apiError = buildApiError(err, 'Failed to save location');
    if (apiError.code === 'INVALID_LOCATION_ID' || apiError.status === 400) {
      apiError.message = 'This place could not be saved — invalid location id.';
    } else if (apiError.status === 404) {
      apiError.message = 'This place could not be found.';
    }
    error('[saveLocation] Failed to bookmark location', {
      locationId,
      status: apiError.status,
      code: apiError.code,
      message: apiError.message,
    });
    throw apiError;
  }
}

/**
 * Remove a location bookmark.
 *
 * Safe to call even when no bookmark exists — `deletedCount` will be 0, no error.
 */
export async function unsaveLocation(
  locationId: string,
): Promise<UnsaveLocationResponse> {
  try {
    debug('[unsaveLocation] Removing location bookmark', { locationId });

    const response = await axios.post<UnsaveLocationResponse>(LOCATION_UNSAVE, {
      locationId,
    });

    debug('[unsaveLocation] Location bookmark removed', {
      locationId,
      deletedCount: response.data.deletedCount,
    });

    return response.data;
  } catch (err) {
    const apiError = buildApiError(err, 'Failed to unsave location');
    error('[unsaveLocation] Failed to remove location bookmark', {
      locationId,
      status: apiError.status,
      message: apiError.message,
    });
    throw apiError;
  }
}

/**
 * Fetch the current user's saved locations feed, sorted most-recently-saved first.
 *
 * The hydrated `location` document is included on each item.
 * Defaults to authenticated user; pass `userId` for public profile views.
 */
export async function fetchSavedLocationsForUser(
  options: FetchSavedLocationsOptions = {},
): Promise<SavedLocationFeedItem[]> {
  const { userId, limit = 50, skip = 0 } = options;
  try {
    const params: Record<string, string | number> = { limit, skip };
    if (userId) params.user_id = userId;

    debug('[fetchSavedLocationsForUser] Fetching saved locations', { userId: userId ?? 'self', limit, skip });

    const response = await axios.get<SavedLocationFeedItem[]>(SAVED_LOCATIONS_FEED, { params });

    const items = Array.isArray(response.data) ? response.data : [];

    debug('[fetchSavedLocationsForUser] Fetched saved locations', { count: items.length });

    return items;
  } catch (err) {
    const apiError = buildApiError(err, 'Failed to fetch saved locations');
    error('[fetchSavedLocationsForUser] Failed', {
      status: apiError.status,
      message: apiError.message,
    });
    throw apiError;
  }
}

/**
 * Get the number of users who have bookmarked a location (inbound saves count).
 */
export async function fetchLocationSaveCount(
  locationId: string,
  options: { browseMode?: PlatformBrowseMode } = {},
): Promise<number> {
  try {
    const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const hasAuth = !!rawAccessToken;
    if (shouldSkipGuestAuthenticatedApiCalls(hasAuth, options.browseMode)) {
      return 0;
    }

    const basePath = shouldUseOpenBrowseGuestEndpoints(hasAuth, options.browseMode, false)
      ? `${API_URL}/api/v2/public/location/${encodeURIComponent(locationId)}/saves/count`
      : `${API_URL}/api/v2/location/${encodeURIComponent(locationId)}/saves/count`;

    const response = await axios.get<LocationSaveCountResponse>(basePath);
    return response.data.count ?? 0;
  } catch (err) {
    const apiError = buildApiError(err, 'Failed to fetch location save count');
    error('[fetchLocationSaveCount] Failed', {
      locationId,
      status: apiError.status,
    });
    throw apiError;
  }
}

/**
 * Get a paginated list of users who saved a location (savers list).
 */
export async function fetchLocationSavers(
  locationId: string,
  options: { limit?: number; skip?: number } = {},
): Promise<LocationSaversResponse> {
  const { limit = 100, skip = 0 } = options;
  try {
    const url = `${API_URL}/api/v2/location/${encodeURIComponent(locationId)}/saves`;
    const response = await axios.get<LocationSaversResponse>(url, {
      params: { limit, skip },
    });
    return response.data;
  } catch (err) {
    const apiError = buildApiError(err, 'Failed to fetch location savers');
    error('[fetchLocationSavers] Failed', { locationId, status: apiError.status });
    throw apiError;
  }
}

/**
 * Get the total number of locations a user has bookmarked (outbound count).
 */
export async function fetchUserLocationSavesOutboundCount(
  userId: string,
): Promise<number> {
  try {
    const url = `${API_URL}/api/v2/user/${encodeURIComponent(userId)}/location-saves/count`;
    const response = await axios.get<UserLocationSavesOutboundCountResponse>(url);
    return response.data.total_location_saves ?? 0;
  } catch (err) {
    const apiError = buildApiError(err, 'Failed to fetch user location saves count');
    error('[fetchUserLocationSavesOutboundCount] Failed', {
      userId,
      status: apiError.status,
    });
    throw apiError;
  }
}

/**
 * Get the total number of inbound location saves the creator's published
 * content has received from **other** users.
 *
 * Uses `GET /api/v2/user/:userId/location-saves/received/count`.
 * `excludeOwn` defaults to `true` (backend default) — pass `false` only if
 * you explicitly want self-saves included.
 *
 * Do NOT use `fetchUserLocationSavesOutboundCount` for this surface — that
 * measures how many places *the user* has bookmarked (outbound).
 */
export async function fetchUserLocationSavesReceivedCount(
  userId: string,
  options: Pick<FetchUserLocationSavesReceivedOptions, 'excludeOwn'> = {},
): Promise<number> {
  try {
    const params: Record<string, boolean> = {};
    if (options.excludeOwn === false) params.exclude_own = false;

    debug('[fetchUserLocationSavesReceivedCount] Fetching inbound count', { userId, ...options });

    const headers = getV2AuthHeaders();
    const url = `${API_URL}/api/v2/user/${encodeURIComponent(userId)}/location-saves/received/count`;
    const response = await axios.get<UserLocationSavesReceivedCountResponse>(url, {
      params,
      headers,
    });

    const count = response.data.total_location_saves_received ?? 0;
    debug('[fetchUserLocationSavesReceivedCount] Fetched inbound count', { userId, count });
    return count;
  } catch (err) {
    const apiError = buildApiError(err, 'Failed to fetch inbound location saves count');
    error('[fetchUserLocationSavesReceivedCount] Failed', {
      userId,
      status: apiError.status,
      message: apiError.message,
    });
    throw apiError;
  }
}

/**
 * Get a paginated list of inbound location saves on the creator's published content.
 *
 * Uses `GET /api/v2/user/:userId/location-saves/received`.
 * Each row includes `location_id`, `source_post_id`, `saver_user_id`, `created_at`, etc.
 * `excludeOwn` defaults to `true` (backend default).
 */
export async function fetchUserLocationSavesReceived(
  userId: string,
  options: FetchUserLocationSavesReceivedOptions = {},
): Promise<UserLocationSavesReceivedResponse> {
  const { excludeOwn, limit = 50, skip = 0 } = options;
  try {
    const params: Record<string, number | boolean> = { limit, skip };
    if (excludeOwn === false) params.exclude_own = false;

    debug('[fetchUserLocationSavesReceived] Fetching inbound location saves', {
      userId,
      limit,
      skip,
      excludeOwn,
    });

    const headers = getV2AuthHeaders();
    const url = `${API_URL}/api/v2/user/${encodeURIComponent(userId)}/location-saves/received`;
    const response = await axios.get<UserLocationSavesReceivedResponse>(url, {
      params,
      headers,
    });

    const total = response.data.total ?? 0;
    const saves = Array.isArray(response.data.saves) ? response.data.saves : [];
    debug('[fetchUserLocationSavesReceived] Fetched inbound location saves', {
      userId,
      total,
      fetchedCount: saves.length,
    });
    return { total, saves };
  } catch (err) {
    const apiError = buildApiError(err, 'Failed to fetch inbound location saves');
    error('[fetchUserLocationSavesReceived] Failed', {
      userId,
      status: apiError.status,
      message: apiError.message,
    });
    throw apiError;
  }
}

/**
 * Build a partial `Location` object from the hydrated `location` field in a
 * `SavedLocationFeedItem`. Used to pre-populate `CombinedContentPlaceModal`
 * without a separate fetch.
 */
export function buildLocationFromFeedItem(
  feedItem: SavedLocationFeedItem,
): Partial<InstanceType<typeof Location>> {
  const raw = feedItem.location;
  if (!raw) return {};
  return new Location(raw as Record<string, unknown>);
}
